import * as React from 'react';

import { Inline } from '~shared/components/Inline';
import { Stack } from '~shared/components/Stack';
import { ReactNodeNoStrings } from '~shared/types';

import { Dialog, DialogProps } from './Dialog';
import { DialogTitle } from './DialogTitle';

type DialogConfirmationAlign = 'left' | 'center' | 'right';

interface Props extends Omit<DialogProps, 'children' | 'padding'> {
  children: React.ReactNode;
  actions: ReactNodeNoStrings[];
  footer?: ReactNodeNoStrings;
  /**
   * Alignment of the title + body text. Defaults to `'center'` (the legacy
   * behaviour). For `'left'`/`'right'` the action buttons move to the right —
   * the conventional placement for a left/right-aligned confirmation dialog —
   * while `'center'` keeps the buttons centered.
   */
  align?: DialogConfirmationAlign;
}

const textAlignClass: Record<DialogConfirmationAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

/**
 * Abstract component for common confirmation dialog styles.
 */
export function DialogConfirmation({
  children,
  actions,
  footer,
  align = 'center',
  ...props
}: Props) {
  const actionsAlign = align === 'center' ? 'center' : 'right';
  return (
    <Dialog padding="confirmation" {...props}>
      {(headerProps) => (
        <div className={textAlignClass[align]}>
          <Stack space="4">
            <h3 className="m-0">
              <DialogTitle
                ref={headerProps.headingRef}
                style={{ paddingRight: 0 }}
              >
                {headerProps.title}
              </DialogTitle>
            </h3>
            <div className="text-base text-blue-900 leading-8">{children}</div>
          </Stack>
          <div className="pt-8">
            <Inline align={actionsAlign}>
              {actions.map((action) => action)}
            </Inline>
          </div>
          {footer && <div className="pt-8">{footer}</div>}
        </div>
      )}
    </Dialog>
  );
}
